import { useState } from 'react';
import { Clock, Play, Square, Loader2 } from 'lucide-react';
import { useDateFormatter } from '@/shared/hooks';
import { useShift } from '../hooks';
import { useAbility } from '@/modules/identity/application/ability';

export const ShiftControls = () => {
  const { currentShift, loading, actionLoading, startShift, endShift, error } = useShift();
  const { formatTime, formatDuration } = useDateFormatter();
  const [notes, setNotes] = useState('');
  const ability = useAbility();

  const canReadShift = ability.can('read', 'Shift') || ability.can('manage', 'all');
  const canStartShift = ability.can('create', 'Shift') || ability.can('manage', 'all');
  const canEndShift = ability.can('update', 'Shift') || ability.can('manage', 'all');

  const handleStartShift = async () => {
    try {
      await startShift(notes || undefined);
      setNotes('');
    } catch {
      // Error handled by hook
    }
  };

  const handleEndShift = async () => {
    try {
      await endShift(notes || undefined);
      setNotes('');
    } catch {
      // Error handled by hook
    }
  };

  if (!canReadShift) {
    return null;
  }

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-center py-4">
          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        </div>
      </div>
    );
  }

  const getElapsedTime = (startTime: string) => {
    const start = new Date(startTime).getTime();
    const now = Date.now();
    const diffHours = (now - start) / 3600000;
    return formatDuration(diffHours);
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-4">
        <Clock className="w-5 h-5 text-primary-500" />
        <h3 className="text-lg font-semibold text-slate-800">Control de Turno</h3>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {currentShift ? (
        <div className="space-y-4">
          <div className="bg-green-50 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-green-600 font-medium">Turno activo</p>
                <p className="text-lg font-bold text-green-700">
                  Desde {formatTime(currentShift.startTime)}
                </p>
                <p className="text-sm text-green-600">
                  Tiempo: {currentShift.duration || getElapsedTime(currentShift.startTime)}
                </p>
              </div>
              <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">
              Notas de cierre (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Incidentes, observaciones..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={2}
            />
          </div>

          <button
            onClick={handleEndShift}
            disabled={actionLoading || !canEndShift}
            className="w-full flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {actionLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Square className="w-5 h-5" />
                Finalizar Turno
              </>
            )}
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="bg-slate-50 rounded-lg p-4">
            <p className="text-sm text-slate-600">Sin turno activo</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1">
              Notas de inicio (opcional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observaciones iniciales..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-500"
              rows={2}
            />
          </div>

          <button
            onClick={handleStartShift}
            disabled={actionLoading || !canStartShift}
            className="w-full flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-medium transition-colors disabled:opacity-50"
          >
            {actionLoading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <Play className="w-5 h-5" />
                Iniciar Turno
              </>
            )}
          </button>
        </div>
      )}
    </div>
  );
};
